// FastAPI Backend Configuration
const FASTAPI_BASE_URL = process.env.REACT_APP_FASTAPI_URL || "http://localhost:8000";
const ORGANIZATION_ID = process.env.REACT_APP_ORGANIZATION_ID || "3fa85f64-5717-4562-b3fc-2c963f66afa6";

// Error handler for API calls
const handleApiError = async (response, context = "API call") => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
    console.error(`${context} failed:`, errorData);
    throw new Error(errorData.detail || errorData.error || `${context} failed`);
  }
  return response.json();
};

// Get auth token from FastAPI backend
export const getToken = async () => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/auth/token`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // Add any auth headers your FastAPI requires
        // "Authorization": "Bearer your-api-key",
      },
    });

    const data = await handleApiError(response, "Token generation");
    return data.token;
  } catch (error) {
    console.error("Error getting token:", error);
    throw error;
  }
};

// Create meeting through FastAPI
export const createMeeting = async ({ token }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/meetings/`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        title: "Quick Meeting",
        description: "Meeting created from VideoSDK app",
        start_time: new Date().toISOString(),
        end_time: new Date(Date.now() + 60 * 60 * 1000).toISOString(), // 1 hour from now
        max_participants: 100,
        settings: {
          created_from: "videosdk_app",
          quick_meeting: true
        },
        webhook_config: {}
      }),
    });

    const data = await handleApiError(response, "Meeting creation");
    return { meetingId: data.room_id, err: null };
  } catch (error) {
    return { meetingId: null, err: error.message };
  }
};

// Validate meeting through FastAPI
export const validateMeeting = async ({ roomId, token }) => {
  try {
    // First, try to get meeting by room_id
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/meetings/validate/${roomId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 404) {
      return { meetingId: null, err: "Meeting not found" };
    }

    const data = await handleApiError(response, "Meeting validation");
    return { meetingId: data.room_id, err: null };
  } catch (error) {
    return { meetingId: null, err: error.message };
  }
};

// Join meeting through FastAPI
export const joinMeeting = async ({ meetingId, userId, participantName, role = "participant" }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/meetings/${meetingId}/join`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_id: userId,
        participant_name: participantName,
        role: role
      }),
    });

    const data = await handleApiError(response, "Meeting join");
    return {
      token: data.token,
      roomId: data.room_id,
      meeting: data.meeting,
      err: null
    };
  } catch (error) {
    return { token: null, roomId: null, meeting: null, err: error.message };
  }
};

// Validate join token
export const validateJoinToken = async ({ token, roomId }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/join/validate-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: token,
        room_id: roomId
      }),
    });

    const data = await handleApiError(response, "Token validation");
    return { valid: true, data: data, err: null };
  } catch (error) {
    return { valid: false, data: null, err: error.message };
  }
};

// Get meeting details
export const getMeetingDetails = async ({ meetingId, token }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/meetings/${meetingId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = await handleApiError(response, "Get meeting details");
    return { meeting: data, err: null };
  } catch (error) {
    return { meeting: null, err: error.message };
  }
};

// Meeting request APIs (for integration flow)
export const getMeetingRequestDetails = async ({ requestId, token }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/meeting-requests/${requestId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = await handleApiError(response, "Get meeting request details");
    return { meetingRequest: data, err: null };
  } catch (error) {
    return { meetingRequest: null, err: error.message };
  }
};

// Host start meeting from request
export const hostStartMeetingFromRequest = async ({ requestId, hostEmail, meetingSettings = {} }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/meeting-requests/${requestId}/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        host_email: hostEmail,
        meeting_settings: meetingSettings
      }),
    });

    const data = await handleApiError(response, "Host start meeting");
    return {
      meeting: data.meeting,
      meetingRequest: data.meeting_request,
      joinToken: data.join_token,
      roomId: data.room_id,
      message: data.message,
      err: null
    };
  } catch (error) {
    return {
      meeting: null,
      meetingRequest: null,
      joinToken: null,
      roomId: null,
      message: null,
      err: error.message
    };
  }
};

// Create user (if needed for guest users)
export const createUser = async ({ name, email, role = "participant" }) => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}/users/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: name,
        email: email,
        role: role,
        metadata: {
          created_from: "videosdk_app"
        }
      }),
    });

    const data = await handleApiError(response, "User creation");
    return { user: data, err: null };
  } catch (error) {
    return { user: null, err: error.message };
  }
};

// Get organization details
export const getOrganizationDetails = async () => {
  try {
    const response = await fetch(`${FASTAPI_BASE_URL}/api/v1/organizations/${ORGANIZATION_ID}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await handleApiError(response, "Get organization details");
    return { organization: data, err: null };
  } catch (error) {
    return { organization: null, err: error.message };
  }
};

// Helper function to extract room ID from different formats
export const extractRoomId = (input) => {
  // Handle different room ID formats
  if (typeof input === "string") {
    // If it's already a proper room ID format (xxxx-xxxx-xxxx)
    if (input.match(/^\w{4}-\w{4}-\w{4}$/)) {
      return input;
    }
    // If it's a UUID, might need transformation
    return input;
  }
  return input;
};

// Helper function to check if running in development
export const isDevelopment = () => {
  return process.env.NODE_ENV === "development";
};

// Fallback functions for backward compatibility
export const createMeetingLegacy = async ({ token }) => {
  // This maintains backward compatibility with VideoSDK direct integration
  // Remove this once fully migrated to FastAPI
  console.warn("Using legacy VideoSDK integration. Consider migrating to FastAPI backend.");
  
  const response = await fetch("https://api.videosdk.live/v2/rooms", {
    method: "POST",
    headers: {
      authorization: `${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  
  const { roomId } = await response.json();
  return { meetingId: roomId, err: null };
};

export const validateMeetingLegacy = async ({ roomId, token }) => {
  // This maintains backward compatibility with VideoSDK direct integration
  console.warn("Using legacy VideoSDK integration. Consider migrating to FastAPI backend.");
  
  const response = await fetch(`https://api.videosdk.live/v2/rooms/validate/${roomId}`, {
    method: "GET",
    headers: {
      authorization: token,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 400) {
    const data = await response.text();
    return { meetingId: null, err: data };
  }

  const data = await response.json();
  
  if (data.roomId) {
    return { meetingId: data.roomId, err: null };
  } else {
    return { meetingId: null, err: data.error };
  }
};
